// The first-integration journey (/onboarding): welcome, domain, how it works,
// then step 1 connect your product, step 2 choose integrations, step 3 embed
// the widget, step 4 customers connect and customize.

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const onboardingTestIds = {
  // Shared chrome
  creditsBadge: 'onboarding-credits-badge',
  creditsToast: 'onboarding-credits-toast',
  stepPill: 'onboarding-step-pill',
  skip: 'onboarding-skip-button',
  back: 'onboarding-back-button',
  doItLater: 'onboarding-do-it-later-button',
  popupClose: 'onboarding-popup-close-button',

  // AI tools pop-up
  connectAiTools: 'onboarding-connect-ai-tools-button',
  aiPopover: 'onboarding-ai-tools-modal',
  aiToolSelect: 'onboarding-ai-tool-dropdown',
  aiToolOption: (tool: string) => `onboarding-ai-tool-${tool}-option` as const,
  aiToolCopyLink: 'onboarding-ai-tool-copy-link-button',
  aiToolAdd: 'onboarding-ai-tool-add-button',
  startHere: 'onboarding-start-here-button',
  finishSetupCard: 'onboarding-finish-setup-modal',
  finishSetupDocs: 'onboarding-finish-setup-docs-link',
  finishSetupGuide: 'onboarding-finish-setup-guide-link',
  finishSetupReopen: 'onboarding-finish-setup-reopen-button',
  finishSetupDone: 'onboarding-finish-setup-done-button',

  // 13 · Skip step 1?
  skipDialog: 'onboarding-skip-modal',
  skipConfirm: 'onboarding-skip-confirm-button',
  skipConnectNow: 'onboarding-skip-connect-now-button',

  // 0c · Welcome
  welcome: 'onboarding-welcome-page',
  welcomeSkip: 'onboarding-welcome-skip-button',
  welcomeBack: 'onboarding-welcome-back-button',
  welcomeNext: 'onboarding-welcome-next-button',
  welcomeStart: 'onboarding-welcome-start-button',

  // 1 · 2 · 2b · Domain
  domainConfirm: 'onboarding-domain-confirm-page',
  domainYes: 'onboarding-domain-yes-button',
  domainChange: 'onboarding-domain-change-button',
  domainDifferent: 'onboarding-domain-different-button',
  domainAsk: 'onboarding-domain-ask-page',
  domainInput: 'onboarding-domain-input',
  domainContinue: 'onboarding-domain-continue-button',
  domainNone: 'onboarding-domain-none-button',
  noProduct: 'onboarding-no-product-page',
  noProductAddDomain: 'onboarding-no-product-add-domain-button',
  noProductLookAround: 'onboarding-no-product-look-around-button',

  // 3 · How it works
  how: 'onboarding-how-page',
  howStep: (n: number) => `onboarding-how-step-${n}-card` as const,
  howDocsLink: 'onboarding-how-docs-link',
  howCreditsLink: 'onboarding-how-credits-link',
  howStepsDocs: 'onboarding-how-steps-docs-link',
  howGetStarted: 'onboarding-how-get-started-button',

  // 4 · Research
  research: 'onboarding-research-page',
  researchBox: 'onboarding-research-results-panel',
  researchMissing: 'onboarding-research-missing-panel',
  researchAddLink: 'onboarding-research-add-link-button',
  researchDocsInput: 'onboarding-research-docs-input',
  researchReview: 'onboarding-research-review-button',
  researchContinue: 'onboarding-research-continue-button',
  researchTellUs: 'onboarding-research-tell-us-button',
  researchExplore: 'onboarding-research-explore-button',
  uploadOpenApi: 'onboarding-upload-openapi-button',

  // 5 · What we found
  review: 'onboarding-review-page',
  reviewRow: (field: string) => `onboarding-review-${field}-row` as const,
  reviewInput: (field: string) => `onboarding-review-${field}-input` as const,
  reviewSave: (field: string) => `onboarding-review-${field}-save-button` as const,
  reviewWrongCompany: 'onboarding-review-wrong-company-button',
  reviewEditAllAi: 'onboarding-review-edit-all-ai-button',
  reviewNoDocs: 'onboarding-review-no-docs-button',
  reviewWebhooks: 'onboarding-review-webhooks-panel',
  reviewWebhooksToggle: 'onboarding-review-webhooks-toggle-button',
  reviewAddWebhook: 'onboarding-review-add-webhook-button',
  reviewNext: 'onboarding-review-next-button',
  reviewAiPanel: 'onboarding-review-ai-panel',
  reviewAiInput: 'onboarding-review-ai-input',
  reviewAiSend: 'onboarding-review-ai-send-button',
  reviewAiWorking: 'onboarding-review-ai-working',
  reviewAiProposal: 'onboarding-review-ai-proposal',
  reviewAiKeep: 'onboarding-review-ai-keep-button',
  reviewAiUndo: 'onboarding-review-ai-undo-button',
  alreadyConnected: 'onboarding-already-connected',
  alreadyContinue: 'onboarding-already-connected-continue-button',
  alreadyUse: 'onboarding-already-connected-use-button',
  alreadyBuildAnyway: 'onboarding-already-connected-build-button',
  alreadyOption: (slugOrName: string) => `onboarding-already-connected-${slug(slugOrName)}-checkbox` as const,
  entitySuggest: (name: string) => `onboarding-entity-${slug(name)}-button` as const,
  entityOwn: 'onboarding-entity-own-button',
  entityOwnInput: 'onboarding-entity-own-input',

  // 4d · Tell us about your product
  about: 'onboarding-about-page',
  aboutWhat: 'onboarding-about-what-textarea',
  aboutDocs: 'onboarding-about-docs-input',
  aboutNoApi: 'onboarding-about-no-api-button',
  aboutNext: 'onboarding-about-next-button',

  // 7 · Which way does your data move?
  direction: 'onboarding-direction-page',
  directionOption: (dir: string) => `onboarding-direction-${dir}-radio` as const,
  directionDocs: 'onboarding-direction-docs-input',
  directionNotSure: 'onboarding-direction-not-sure-button',
  directionContinue: 'onboarding-direction-continue-button',
  directionInOutDocs: 'onboarding-direction-in-out-docs-link',
  researchDocs: 'onboarding-research-docs-link',
  reviewDocs: 'onboarding-review-docs-link',
  aboutStepDocs: 'onboarding-about-step-docs-link',
  directionDocsHint: 'onboarding-direction-docs-hint',

  // 8 · Tell us your use case
  useCase: 'onboarding-use-case-page',
  useCaseInput: 'onboarding-use-case-textarea',
  useCaseHopes: 'onboarding-use-case-hopes-textarea',
  useCaseSend: 'onboarding-use-case-send-button',
  useCaseGuide: 'onboarding-use-case-guide-link',

  // 5e · Connect your product to fastn
  build: 'onboarding-build-page',
  buildChecking: 'onboarding-build-checking',
  buildOption: (id: string) => `onboarding-build-${id}-radio` as const,
  /** On an option the AI credits left do not cover: where to get more. */
  buildUpgrade: (id: string) => `onboarding-build-${id}-upgrade-link` as const,
  integrationUpgrade: 'onboarding-integration-upgrade-link',
  buildWithTools: 'onboarding-build-with-tools-button',
  buildStart: 'onboarding-build-start-button',
  buildWaysDocs: 'onboarding-build-ways-docs-link',

  // 5f · No API docs: the build has nothing to read
  docsNeeded: 'onboarding-docs-needed-page',
  docsNeededDocs: 'onboarding-docs-needed-docs-link',
  docsNeededInput: 'onboarding-docs-needed-docs-input',
  docsNeededContinue: 'onboarding-docs-needed-continue-button',
  docsNeededAccess: 'onboarding-docs-needed-access-button',
  docsNeededSupport: 'onboarding-docs-needed-support-link',

  // 10 · 10q · 10d · The agent connects the product
  productBuilding: 'onboarding-product-building-page',
  productBuilt: 'onboarding-product-built-page',
  productBuiltContinue: 'onboarding-product-built-continue-button',
  productBuildFailed: 'onboarding-product-build-failed-page',
  productBuildBlocked: 'onboarding-product-build-blocked-page',
  buildBlockedFixDocs: 'onboarding-build-blocked-fix-docs-button',
  buildFailedAskAgent: 'onboarding-build-failed-ask-agent-button',
  buildFailedRetry: 'onboarding-build-failed-retry-button',
  buildTodo: 'onboarding-build-todo-row',
  buildMinimize: 'onboarding-build-minimize-button',
  buildWaiting: 'onboarding-build-waiting-status',
  agentChatToggle: 'onboarding-agent-chat-toggle-button',
  agentChat: 'onboarding-agent-chat-panel',
  agentChatExpand: 'onboarding-agent-chat-expand-button',
  agentQuestion: 'onboarding-agent-question-panel',
  agentQuickReply: (label: string) => `onboarding-agent-reply-${slug(label)}-button` as const,
  agentReply: 'onboarding-agent-reply-input',
  agentSend: 'onboarding-agent-send-button',
  agentSendError: 'onboarding-agent-send-error',
  agentThinking: 'onboarding-agent-thinking-status',
  agentMessage: (role: 'user' | 'assistant') => `onboarding-agent-${role}-message` as const,
  agentConnect: 'onboarding-agent-connect-button',
  agentConnectDrawer: 'onboarding-agent-connect-drawer',
  agentConnectClose: 'onboarding-agent-connect-close-button',
  agentPresented: (i: number) => `onboarding-agent-presented-${i}-button` as const,
  agentPresentDrawer: 'onboarding-agent-present-drawer',

  // 10b · 10c · 10c2 · Step 2
  integrations: 'onboarding-integrations-page',
  enableAll: 'onboarding-integrations-enable-all-button',
  integrationTile: (name: string) => `onboarding-integration-${slug(name)}-button` as const,
  integrationUseCases: 'onboarding-integration-use-cases-panel',
  useCaseOption: (i: number) => `onboarding-integration-use-case-${i}-radio` as const,
  useCaseOther: 'onboarding-integration-use-case-other-input',
  integrationAiTools: (key: string) => `onboarding-integration-${slug(key)}-ai-tools-button` as const,
  integrationBuild: 'onboarding-integration-build-button',
  integrationMissing: 'onboarding-integration-missing-panel',
  integrationAgentStart: 'onboarding-integration-agent-start-button',
  integrationRequest: 'onboarding-integration-request-button',
  integrationSkip: 'onboarding-integrations-skip-button',
  integrationsDocs: 'onboarding-integrations-docs-link',

  // 10d2 · Building the integration
  integrationOpenBuild: 'onboarding-integration-open-build-button',
  integrationBuilding: 'onboarding-integration-building-page',
  integrationPart: (part: string) => `onboarding-integration-part-${slug(part)}-row` as const,
  integrationContinue: 'onboarding-integration-continue-button',
  integrationBuilt: (kind: string, id: string) => `onboarding-integration-built-${slug(kind)}-${slug(id)}-link` as const,
  integrationMinimize: 'onboarding-integration-minimize-button',
  integrationPartsDocs: 'onboarding-integration-parts-docs-link',
  integrationAskAgent: 'onboarding-integration-ask-agent-button',
  integrationStopped: 'onboarding-integration-stopped-panel',
  integrationOpenAgent: 'onboarding-integration-open-agent-button',
  integrationSeeAgent: 'onboarding-integration-see-agent-button',
  integrationAgentMessage: 'onboarding-integration-agent-message-toggle',
  integrationQuestionsOpen: 'onboarding-integration-questions-open-button',
  integrationQuestions: 'onboarding-integration-questions-modal',
  integrationQuestionsLater: 'onboarding-integration-questions-later-button',
  integrationQuestionsBack: 'onboarding-integration-questions-back-button',
  integrationQuestionOption: (i: number) => `onboarding-integration-question-option-${i}-button` as const,
  integrationQuestionOther: 'onboarding-integration-question-other-button',
  integrationQuestionOtherInput: 'onboarding-integration-question-other-input',
  integrationQuestionSave: 'onboarding-integration-question-save-button',
  integrationQuestionNext: 'onboarding-integration-question-next-button',
  integrationConfigOpen: 'onboarding-integration-config-open-button',
  integrationConfigDrawer: 'onboarding-integration-config-drawer',
  integrationConfigClose: 'onboarding-integration-config-close-button',

  // 10e · Step 3
  embed: 'onboarding-embed-page',
  embedGuide: 'onboarding-embed-guide-link',
  customersEmbedDocs: 'onboarding-customers-embed-docs-link',
  embedTiersLink: 'onboarding-embed-tiers-link',
  embedPreview: 'onboarding-embed-preview-button',
  embedCustomerName: 'onboarding-embed-customer-name-input',
  embedNeedKey: 'onboarding-embed-need-key-status',
  embedGoToKey: 'onboarding-embed-go-to-key-button',
  embedCustomerId: 'onboarding-embed-customer-id-input',
  embedPreviewDialog: 'onboarding-embed-preview-dialog',
  embedPreviewClose: 'onboarding-embed-preview-close-button',
  embedPreviewFrame: 'onboarding-embed-preview-frame',
  customersCustomizeDocs: 'onboarding-customers-customize-docs-link',
  customersOpenProduct: 'onboarding-customers-open-product-link',
  customersFeed: 'onboarding-customers-feed',
  customersEvent: (id: string) => `onboarding-customers-event-${id}` as const,
  embedStepToggle: (n: number) => `onboarding-embed-step-${n}-toggle-button` as const,
  embedStepDocs: (n: number) => `onboarding-embed-step-${n}-docs-link` as const,
  embedDevLink: 'onboarding-embed-dev-link-button',
  embedWaiting: 'onboarding-embed-waiting-status',
  embedWaitingHint: 'onboarding-embed-waiting-hint',
  /** The backend calls from the embedding guide, folded under the snippet. */
  /** Step 3's guide rows, each with data-state done / wait / todo / blocked. */
  embedRow: (part: string) => `onboarding-embed-${part}-row` as const,
  embedCreateKey: 'onboarding-embed-create-api-key-button',
  embedCustomizeLook: 'onboarding-embed-customize-look-link',
  embedBackendCopy: (call: string) => `onboarding-embed-backend-${call}-copy-button` as const,
  embedApiKeysLink: 'onboarding-embed-api-keys-link',
  embedFound: 'onboarding-embed-found-status',
  embedContinue: 'onboarding-embed-continue-button',

  // 10f · 10g · Step 4
  customers: 'onboarding-customers-page',
  /** Step 4 tracks the registered customers, each with data-state connected / waiting. */
  customersWaiting: 'onboarding-customers-waiting-status',
  /** The real widget, loaded as the picked test customer. */
  customerConnected: 'onboarding-customer-connected-status',
  customersFinish: 'onboarding-customers-finish-button',
  done: 'onboarding-done-modal',
  doneSecond: 'onboarding-done-second-button',
  doneLater: 'onboarding-done-later-button',
} as const;
